/**
 * @fileoverview Inline slide-over drawer palette (`CanvasWidgetPalette`) for adding widgets
 * to `DashboardCanvasBuilder` (replaces the legacy sidebar tab `WidgetPalette`).
 * @module ui-admin/components/DashboardCanvasBuilder/CanvasWidgetPalette
 *
 * Responsibilities: - Render categorized widget types inside `SlideOverPanel` with search filtering.
 *                   - Invoke `onAddWidget(type)` and close when a widget type is clicked.
 * Dependencies:     - React, lucide-react
 *                   - ../SlideOverPanel/SlideOverPanel (SlideOverPanel)
 *                   - ./widgetTypes (WIDGET_TYPES, WIDGET_CATEGORIES, WidgetType)
 * Architecture:     - Presentation-only drawer component; zero data access.
 * Notes:            - Closes gap G-06.
 */

import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { SlideOverPanel } from '../SlideOverPanel/SlideOverPanel';
import { WIDGET_TYPES, WIDGET_CATEGORIES, type WidgetType } from './widgetTypes';

/**
 * @contract CanvasWidgetPaletteProps
 * Purpose: Props for `CanvasWidgetPalette`.
 * Fields:  open, onClose, onAddWidget
 * Rules:   Clicking a widget option calls `onAddWidget(type)` and `onClose()`.
 * Sync:    N/A.
 */
export interface CanvasWidgetPaletteProps {
  open: boolean;
  onClose: () => void;
  onAddWidget: (type: WidgetType) => void;
}

/**
 * @component CanvasWidgetPalette
 * Purpose:     Inline drawer palette for adding widgets to the dashboard canvas.
 * Props:       CanvasWidgetPaletteProps.
 * Behavior:    Groups `WIDGET_TYPES` by category with search filtering; clicking a type adds it and closes the drawer.
 * Data Source: none (static `WIDGET_TYPES`).
 * Constraints: presentation-only.
 * Performance: filtered list memoized on search query.
 */
export function CanvasWidgetPalette({
  open,
  onClose,
  onAddWidget,
}: CanvasWidgetPaletteProps) {
  const [search, setSearch] = useState('');

  const filteredByCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    return WIDGET_CATEGORIES.map((category) => ({
      category,
      items: WIDGET_TYPES.filter(
        (w) =>
          w.category === category &&
          (!q ||
            w.label.toLowerCase().includes(q) ||
            w.description.toLowerCase().includes(q) ||
            w.type.toLowerCase().includes(q)),
      ),
    })).filter((g) => g.items.length > 0);
  }, [search]);

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Add Widget to Dashboard"
      width={360}
      side="left"
    >
      <div className="p-4 space-y-4" data-testid="canvas-widget-palette">
        <div className="relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search widgets (KPI, chart, table, filter...)"
            aria-label="Search widgets"
            className="w-full pl-9 pr-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-orange-500"
          />
        </div>

        {filteredByCategory.map(({ category, items }) => (
          <div key={category} className="space-y-1.5">
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {category}
            </h4>
            <div className="space-y-1.5">
              {items.map((wt) => {
                const Icon = wt.icon;
                return (
                  <button
                    key={wt.type}
                    type="button"
                    data-testid={`palette-widget-${wt.type}`}
                    onClick={() => {
                      onAddWidget(wt.type);
                      onClose();
                    }}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 hover:border-orange-400 hover:bg-orange-50/50 transition-all text-left"
                  >
                    <div className="p-2 rounded-md bg-gray-100 text-gray-700 shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-900">{wt.label}</div>
                      <div className="text-[11px] text-gray-500 truncate">{wt.description}</div>
                    </div>
                    <span className="text-[10px] font-medium text-gray-400 shrink-0">
                      {`${wt.defaultW}×${wt.defaultH}`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </SlideOverPanel>
  );
}

export default CanvasWidgetPalette;
